import { NavigateFunction, NavigateOptions, To, useNavigate } from "react-router-dom";

import { Routes } from "@ctypes";
import { AUTH_STEPS, ONBOARDING_STEPS } from "@entry/steps";

/**
 * Routes in AUTH_STEPS/ONBOARDING_STEPS only exist as internal step keys
 * inside the entry flow's state machine (mounted at /auth and /onboarding)
 * — navigating to them directly with react-router has no matching route
 * and 404s. This is a drop-in replacement for react-router's useNavigate
 * that resolves the correct entry point and carries the target step
 * through as the flow's initialStep, so authenticated app screens can
 * safely jump into a specific onboarding/auth step.
 */
export const useNavigateToStep = (): NavigateFunction => {
  const navigate = useNavigate();

  return ((to: To | number, options?: NavigateOptions) => {
    if (typeof to === "number") {
      navigate(to);
      return;
    }

    const step = typeof to === "string" ? to : null;
    const entryPoint = step && AUTH_STEPS.has(step)
      ? Routes.Auth
      : step && ONBOARDING_STEPS.has(step)
        ? Routes.Onboarding
        : null;

    if (!entryPoint) {
      navigate(to, options);
      return;
    }

    navigate(entryPoint, {
      ...options,
      state: {
        ...(typeof options?.state === "object" ? options.state : {}),
        initialStep: step,
      },
    });
  }) as NavigateFunction;
};
